import {
  type CraftJobNetworkSnapshot,
  getItemIcon,
  getRecipeById,
  type ItemTypeValue,
  type RecipeDefinition,
  type TranslationKey,
  type WorkstationFuelNetworkSnapshot,
  type WorkstationTypeValue,
} from '@dreamgame/shared';

const WORKSTATION_TITLE_KEYS: Partial<Record<WorkstationTypeValue, TranslationKey>> = {
  crafting_table: 'ui.workstations.craftingTable',
  anvil: 'ui.workstations.anvil',
  furnace: 'ui.workstations.furnace',
  campfire: 'ui.workstations.campfire',
  cooking_stove: 'ui.workstations.cookingStove',
};

const clampRatio = ({ value }: { value: number }): number => {
  return Math.max(0, Math.min(1, value));
};

export const getWorkstationTitleKey = ({
  workstationType,
}: {
  workstationType: WorkstationTypeValue | null | undefined;
}): TranslationKey => {
  if (!workstationType) {
    return 'ui.workstations.workstation';
  }
  return WORKSTATION_TITLE_KEYS[workstationType] ?? 'ui.workstations.workstation';
};

export const getBurnRemainingMsFromServerTimeline = ({
  fuel,
  currentServerTick,
  currentServerTickIntervalMs,
  currentServerTickUpdatedAtMs,
  nowMs,
}: {
  fuel: Pick<
    WorkstationFuelNetworkSnapshot,
    'burnDurationMs' | 'burnRemainingMs' | 'burnStartedAtTick'
  > | null;
  currentServerTick: number;
  currentServerTickIntervalMs: number;
  currentServerTickUpdatedAtMs: number;
  nowMs: number;
}): number => {
  if (!fuel || fuel.burnDurationMs <= 0 || fuel.burnStartedAtTick < 0) {
    return 0;
  }

  if (currentServerTickUpdatedAtMs <= 0 || currentServerTickIntervalMs <= 0) {
    return Math.max(0, Math.min(fuel.burnDurationMs, fuel.burnRemainingMs));
  }

  const elapsedSinceServerTickMs = Math.max(0, nowMs - currentServerTickUpdatedAtMs);
  const estimatedServerTick =
    currentServerTick + elapsedSinceServerTickMs / currentServerTickIntervalMs;
  const elapsedBurnMs = Math.max(
    0,
    (estimatedServerTick - fuel.burnStartedAtTick) * currentServerTickIntervalMs
  );
  return Math.max(0, fuel.burnDurationMs - elapsedBurnMs);
};

export const getBurnProgressRatio = ({
  displayBurnRemainingMs,
  burnDurationMs,
}: {
  displayBurnRemainingMs: number;
  burnDurationMs: number;
}): number => {
  if (burnDurationMs <= 0) {
    return 0;
  }
  return clampRatio({ value: displayBurnRemainingMs / burnDurationMs });
};

export const getFuelIconPath = ({
  fuel,
}: {
  fuel: WorkstationFuelNetworkSnapshot | null;
}): string | null => {
  if (!fuel || fuel.fuelUnits <= 0) {
    return null;
  }

  const primaryFuelItemType = fuel.acceptedFuelItems[0]?.itemType as ItemTypeValue | undefined;
  if (!primaryFuelItemType) {
    return null;
  }

  const iconName = getItemIcon({ itemType: primaryFuelItemType });
  return iconName ? `/items/${iconName}` : null;
};

export const getCraftCompletionsNeededForDesiredOutput = ({
  recipe,
  desiredOutputQuantity,
}: {
  recipe: RecipeDefinition;
  desiredOutputQuantity: number;
}): number => {
  const outputPerCraft = recipe.outputs[0]?.quantity ?? 1;
  return Math.ceil(desiredOutputQuantity / outputPerCraft);
};

export const getCraftOrderOutputTotals = ({
  job,
}: {
  job: CraftJobNetworkSnapshot;
}): {
  totalOutputQuantity: number;
  completedOutputQuantity: number;
} => {
  const recipe = getRecipeById({ recipeId: job.recipeId });
  const outputPerCraft = recipe?.outputs[0]?.quantity ?? 1;
  return {
    totalOutputQuantity: job.quantity * outputPerCraft,
    completedOutputQuantity: job.quantityCompleted * outputPerCraft,
  };
};

export const getRemainingQueueOutputQuantity = ({
  queue,
}: {
  queue: ReadonlyArray<CraftJobNetworkSnapshot>;
}): number => {
  return queue.reduce((total, job) => {
    const recipe = getRecipeById({ recipeId: job.recipeId });
    const outputPerCraft = recipe?.outputs[0]?.quantity ?? 1;
    const remainingCrafts = Math.max(0, job.quantity - job.quantityCompleted);
    return total + remainingCrafts * outputPerCraft;
  }, 0);
};

export const getInterpolatedCraftProgress = ({
  serverProgress,
  elapsedMs,
  craftTimeMs,
}: {
  serverProgress: number;
  elapsedMs: number;
  craftTimeMs: number;
}): number => {
  if (craftTimeMs <= 0) {
    return clampRatio({ value: serverProgress });
  }
  const expectedIncrement = elapsedMs / craftTimeMs;
  return clampRatio({ value: serverProgress + expectedIncrement });
};
